import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchAllUser } from '@/api/Admin/User';
import { ListUserAPI } from '@/api/Admin/type';
import { RoleModal } from '@/components/Account/RoleModal';
import EyeIcon from '@/components/Icon/EyeIcon';
import VerticalIcon from '@/components/Icon/VerticalIcon';
import TrashIcon from '@/components/Icon/TrashIcon';
import { toast } from 'react-toastify';

const ListUserPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [selectedRoleUserId, setSelectedRoleUserId] = useState<string | null>(null);
  
  // State phục vụ Search & Filter
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>(''); // '' | 'elite' | 'founding'

  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Thêm selectedProgram vào hàm load để trigger gọi API khi filter đổi
  const loadUsers = async (page: number, limit: number, programFilter?: string) => {
    setLoading(true);
    try {
      const response: ListUserAPI = await fetchAllUser(page, limit, programFilter || undefined);
      if (response.status) {
        setUsers(response.data.items);
        setTotal(response.data.total);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  // Gọi lại API khi thay đổi trang, size trang hoặc filter chương trình
  useEffect(() => {
    loadUsers(currentPage, pageSize, selectedProgram);
  }, [currentPage, pageSize, selectedProgram]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = (userId: number) => {
    if (window.confirm(t('accounts.deleteConfirm'))) {
      toast.success(t('accounts.deleteSuccess'));
      loadUsers(currentPage, pageSize, selectedProgram);
    }
  };

  // Filter client-side cho ô tìm kiếm (Tìm kiếm theo Tên hoặc Email)
  const filteredUsers = users.filter((user) => {
    const matchesSearch = 
      user.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const menuItems = (record: any) => [
    {
      label: t('accounts.menu.editProfile'),
      onClick: () => navigate(`/admin/accounts/edit/${record.id}`),
    },
    {
      label: t('accounts.menu.viewTransactions'),
      onClick: () => navigate(`/admin/investors/${record.id}?tab=transactions`),
    },
    {
      label: t('accounts.menu.viewPackages'),
      onClick: () => navigate(`/admin/investors/${record.id}?tab=investment`),
    },
    {
      label: t('accounts.menu.addPackage'),
      onClick: () => navigate(`/admin/users/register-package?user_id=${record.id}`),
    },
    {
      label: t('accounts.menu.changeRole'),
      onClick: () => setSelectedRoleUserId(String(record.id)),
    },
  ];

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <div className="p-6 bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-neutral-100">{t('accounts.title')}</h2>
            <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">{t('accounts.subtitle', { total })}</p>
          </div>
          <button
            onClick={() => navigate('/admin/accounts/create')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center shadow-sm"
          >
            <span className="mr-2 text-lg">+</span> {t('accounts.newAccount')}
          </button>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-neutral-300 text-sm rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={selectedProgram}
              onChange={(e) => {
                setSelectedProgram(e.target.value);
                setCurrentPage(1); // Reset về trang 1 khi đổi bộ lọc
              }}
              className="w-full px-4 py-2 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-neutral-300 text-sm rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            >
              <option value="">Tất cả Program</option>
              <option value="elite">Elite</option>
              <option value="founding">Founder</option>
            </select>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto relative">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-neutral-800/50 border-b border-gray-200 dark:border-neutral-800">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider w-20">{t('accounts.table.id')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">{t('accounts.table.fullName')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">{t('accounts.table.email')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">{t('accounts.table.phone')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Program</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">{t('accounts.table.role')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">{t('accounts.table.status')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider text-right">{t('accounts.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500 dark:text-neutral-400">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('accounts.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500 dark:text-neutral-400">
                    {t('accounts.noData')}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800/30 transition-colors duration-150">
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-neutral-400">{user.id}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-800 dark:text-neutral-200">{user.fullname}</td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-neutral-400">{user.email}</td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-neutral-400">{user.phone}</td>
                    
                    {/* Cột Program mới thêm */}
                    <td className="px-4 py-4 text-sm">
                      {user.program ? (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-md border uppercase ${
                          user.program.toLowerCase() === 'elite'
                            ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-900/30'
                            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-900/30'
                        }`}>
                          {user.program}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-neutral-600">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full border border-blue-100 dark:border-blue-900/30">
                        {user.roles_display || t('accounts.defaultRole')}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${user.is_active === 1
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-100 dark:border-green-900/30'
                          : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/30'
                        }`}>
                        {user.is_active === 1 ? t('accounts.active') : t('accounts.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/accounts/view/${user.id}`)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:text-neutral-500 dark:hover:text-blue-400 transition-colors"
                          title={t('accounts.viewDetail')}
                        >
                          <EyeIcon />
                        </button>

                        <div className="relative" ref={openDropdownId === user.id ? dropdownRef : null}>
                          <button
                            onClick={() => setOpenDropdownId(openDropdownId === user.id ? null : user.id)}
                            className={`p-1.5 text-gray-400 dark:text-neutral-500 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded transition-colors ${openDropdownId === user.id ? 'bg-gray-100 dark:bg-neutral-800' : ''}`}
                          >
                            <VerticalIcon />
                          </button>

                          {openDropdownId === user.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-gray-200 dark:border-neutral-700 z-50 py-1 overflow-hidden animate-in fade-in zoom-in duration-100">
                              {menuItems(user).map((item, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    item.onClick();
                                    setOpenDropdownId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-neutral-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:text-neutral-500 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-neutral-400">{t('accounts.pagination.rowsPerPage')}</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-neutral-300 text-sm rounded-md px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 transition-all"
            >
              {[5, 10, 20, 50].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
              className="p-2 border border-gray-300 dark:border-neutral-700 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-neutral-400 transition-colors"
            >
              &lt;
            </button>
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">{t('accounts.pagination.pageOf', { current: currentPage, total: totalPages })}</span>
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
              className="p-2 border border-gray-300 dark:border-neutral-700 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-neutral-400 transition-colors"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      <RoleModal  
        isOpen={selectedRoleUserId !== null}
        user={users.find(user => String(user.id) === selectedRoleUserId) || { id: '', email: '', name: '', role: '' }}
        onClose={() => {
          setSelectedRoleUserId(null);
          loadUsers(currentPage, pageSize, selectedProgram);
        }}
      />
    </>
  );
};

export default ListUserPage;